import * as fs from 'fs';
import * as os from 'os';
import * as path from 'path';

import type { CartesianPose } from '@sb/geometry';
import type { FTPServer } from '@sb/integrations/implementations/FTPServer/implementation/FTPServer';
import type { S3Bucket } from '@sb/integrations/implementations/S3Bucket/implementation/S3Bucket';
import type { DeviceCommand } from '@sb/integrations/types';
import type { CameraIntrinsicsExtended } from '@sb/integrations/types/cameraTypes';
import { makeNamespacedLog } from '@sb/log';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { LocateResult } from '@sb/routine-runner/vision/LocateTypes';
import type { ProjectedGrasp } from '@sb/routine-runner/vision/projectGrasp';

import type { CapturingStepKind } from './captureFileName';
import {
  CAPTURE_FILENAME_PREFIX,
  generatedCaptureName,
} from './captureFileName';

const log = makeNamespacedLog('captureUtils');

/**
 * Result returned when image validation fails.
 */
export interface ImageValidationFailure {
  failure: { kind: typeof FailureKind.CameraFailure };
  failureReason: string;
}

/**
 * Validates that the image buffer is non-empty and meets a minimum size.
 * Returns a failure result if the image is invalid, or `null` if it is valid.
 */
export function validateImage(
  image: ArrayBuffer,
): ImageValidationFailure | null {
  if (!image || image.byteLength === 0) {
    return {
      failure: { kind: FailureKind.CameraFailure },
      failureReason: 'Received empty image data from camera',
    };
  }

  // Most image formats need at least a few bytes for headers
  if (image.byteLength < 2) {
    return {
      failure: { kind: FailureKind.CameraFailure },
      failureReason: 'Received incomplete image data (too small)',
    };
  }

  return null;
}

/**
 * Converts an image buffer to PNG using sharp and saves it to a temporary file.
 * Returns the path to the temporary file.
 *
 * Throws on format/decoding errors with a descriptive message including
 * `FailureKind.CameraFailure` context, or re-throws unexpected errors.
 */
export async function convertImageToPng(image: ArrayBuffer): Promise<string> {
  const tempFilename = `capture_${Date.now()}_${Math.random().toString(36).substring(7)}.png`;
  const tempFilePath = path.join(os.tmpdir(), tempFilename);

  // `sharp` is loaded lazily, not at module top-level: it's a native addon, and
  // merely importing this module (for `validateImage`, the metadata helpers, the
  // exported types, or any step-schema graph that transitively reaches here) must
  // not trigger the native binary load. Under vitest's worker pool that eager load
  // fails with "Module did not self-register", killing every suite that imports the
  // routine/step graph even though it never converts an image. Deferring the import
  // to the one code path that actually needs sharp keeps those consumers clean.
  const { default: sharp } = await import('sharp');

  try {
    await sharp(image).png().toFile(tempFilePath);
  } catch (error) {
    log.error('convertImageToPng', 'Error converting image with sharp', error);

    if (
      error instanceof Error &&
      (error.message.includes(
        'Input buffer contains unsupported image format',
      ) ||
        error.message.includes('Premature end') ||
        error.message.includes('Vips') ||
        error.message.includes('unsupported') ||
        error.message.includes('corrupt'))
    ) {
      const cameraError = new Error(
        `Image data is corrupted, incomplete, or in an unsupported format: ${error.message}. This may be due to network issues or camera encoding problems.`,
      );

      (cameraError as any).failureKind = FailureKind.CameraFailure;

      throw cameraError;
    }

    throw error;
  }

  log.info('convertImageToPng', 'Converted and saved PNG image to temp file', {
    tempFilePath,
  });

  return tempFilePath;
}

/**
 * Storage equipment type: must expose `kind` and `executeCommand`.
 */
export interface StorageEquipment {
  kind: string;
  executeCommand(command: DeviceCommand): Promise<unknown>;
}

/**
 * Uploads a local file to remote storage (FTP or S3).
 */
export async function uploadToStorage(args: {
  tempFilePath: string;
  remotePath: string;
  storageEquipment: StorageEquipment;
}): Promise<void> {
  const { tempFilePath, remotePath, storageEquipment } = args;
  const equipmentKind = storageEquipment.kind;

  let uploadCommand: DeviceCommand;

  if (equipmentKind === 'FTPServer') {
    uploadCommand = {
      kind: 'FTPServerCommand',
      command: {
        command: 'uploadFile',
        localPath: tempFilePath,
        remotePath,
      },
    };
  } else if (equipmentKind === 'S3Bucket') {
    uploadCommand = {
      kind: 'S3BucketCommand',
      command: {
        command: 'uploadFile',
        localPath: tempFilePath,
        remotePath,
      },
    };
  } else {
    throw new Error(`Unsupported storage equipment type: ${equipmentKind}`);
  }

  log.info('uploadToStorage', 'Uploading image to storage', {
    remotePath,
    equipmentKind,
  });

  try {
    if (equipmentKind === 'FTPServer') {
      await (storageEquipment as unknown as FTPServer).executeCommand(
        uploadCommand,
      );
    } else if (equipmentKind === 'S3Bucket') {
      await (storageEquipment as unknown as S3Bucket).executeCommand(
        uploadCommand,
      );
    }
  } catch (uploadError) {
    const errorMessage =
      uploadError instanceof Error
        ? uploadError.message
        : 'Unknown upload error';

    log.error('uploadToStorage', 'Upload failed', {
      error: uploadError,
      remotePath,
      equipmentKind,
    });

    throw new Error(`Failed to upload image to storage: ${errorMessage}`);
  }
}

/**
 * Formats a `Date` into a capture-friendly timestamp string:
 * `YYYY-MM-DD_HH-mm-ss.SSS`
 */
export function formatCaptureTimestamp(date: Date): string {
  const pad = (n: number, w = 2) => n.toString().padStart(w, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}_${pad(date.getHours())}-${pad(date.getMinutes())}-${pad(
    date.getSeconds(),
  )}.${pad(date.getMilliseconds(), 3)}`;
}

/** The directory under the captures root that a step's frames live in. */
export function captureKindDirectory(
  stepKind: CaptureMetadata['stepKind'],
): string {
  return CAPTURE_FILENAME_PREFIX[stepKind];
}

/**
 * The filename a capture writes when the step configures no filepath.
 *
 * A BARE FILENAME, never a path. Every target already defines its own base —
 * `FTPServer` joins its `basePath`, `S3Bucket` prefixes its `keyPrefix`, and a
 * local target joins its configured directory — so a filename lands inside
 * whatever the target defines and cannot reach outside it.
 *
 * `skillId` is nonetheless collapsed through `UNSAFE_FILENAME_CHARS`, and that
 * is deliberate belt-and-braces rather than a live case. It is safe today by an
 * invariant that lives nowhere near here: `skillId` is typed only as
 * `zod.string()`, and what actually keeps it alphanumeric is that the step
 * resolves it against a real persisted row first, and that a row's id is
 * server-issued — `DatabaseService._create` overwrites any client-supplied id
 * with `getUID()`. Relying on that from inside a filename builder means a
 * change in skill creation could open a traversal here, since
 * `FTPServer.uploadFile` does `path.join(basePath, remotePath)` and `path.join`
 * resolves `..` inside a single argument. `captureFileName` collapsing every
 * part makes the guarantee local instead.
 *
 * Unique per capture through the timestamp, which carries milliseconds. That
 * matters because an operator-written path is free to be constant, and a
 * constant path silently overwrites the same file on every capture — the
 * generated default cannot.
 *
 * The model is deliberately NOT in here. Which workflow judged a part belongs
 * in the record, where a QA system can parse it, rather than in a filename a
 * human reads.
 */
export function generateCaptureFilename(args: {
  stepKind: CaptureMetadata['stepKind'];
  skillId?: string;
  /** The `captureTimestamp` already published to the routine. */
  timestamp: string;
}): string {
  return `${generatedCaptureName(args)}.png`;
}

/**
 * High-level function that validates an image, converts it to PNG,
 * uploads it to storage, and cleans up. Combines `validateImage`,
 * `convertImageToPng`, and `uploadToStorage` into a single call.
 *
 * Returns an `ImageValidationFailure` if the image is invalid, or `null`
 * on success.
 */
export async function saveImageToStorage(args: {
  image: ArrayBuffer;
  remotePath: string;
  storageEquipment: StorageEquipment;
}): Promise<ImageValidationFailure | null> {
  const { image, remotePath, storageEquipment } = args;

  const validationFailure = validateImage(image);

  if (validationFailure) {
    return validationFailure;
  }

  const tempFilePath = await convertImageToPng(image);

  try {
    await uploadToStorage({
      tempFilePath,
      remotePath,
      storageEquipment,
    });
  } finally {
    await fs.promises.unlink(tempFilePath).catch((unlinkError) => {
      log.warn('saveImageToStorage', 'Failed to delete temporary file', {
        tempFilePath,
        error: unlinkError,
      });
    });
  }

  return null;
}

/**
 * Metadata saved alongside a captured image for Locate and Capture steps.
 */
/**
 * One detection in an Inspect record.
 *
 * Leaner than `LocateResult` on purpose. There is no `pose` or `extents`
 * because Inspect is 2D and projects nothing, and no `rotation` because
 * `Detection2D`'s own docblock ties that field to grasp-axis selection — a
 * defect record carrying it would publish a measurement nothing took.
 *
 * `className` is Roboflow's name for the detection, which is what a QA reader
 * recognises. The per-class counts beside this are keyed by the skill's class
 * ids, which is what a routine branches on. The record carries both so it stays
 * legible to a person AND reconcilable against the `count_<id>` variables the
 * step published for the same frame.
 */
export interface InspectDetectionRecord {
  className?: string;
  score: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * What an Inspect step saw in one frame.
 *
 * `detections` is EVERY detection that survived thresholding, including any
 * whose class the skill does not list. The counts cover only the classes it
 * does list, so a reader comparing the two can see that the model emitted
 * something the class list does not describe — which a workflow edited in
 * Roboflow after import will do, and which the step only logs.
 *
 * Per-class thresholds are deliberately absent: they are our filtering policy
 * rather than an observation about the part, and nothing downstream consumes
 * them. Roboflow's own per-inference detection id is absent for a related
 * reason — it is minted per inference, so it identifies no defect across two
 * frames of the same part.
 */
export interface InspectResults {
  detections: InspectDetectionRecord[];
  /** Keyed by `InspectClass.id`, matching the step's `count_<id>` variables. */
  countsByClassId: Record<string, number>;
  /**
   * Which Roboflow workflow produced these detections, and which of its outputs
   * was read.
   *
   * Recorded because a skill is resolved fresh on every execution of the step,
   * so re-importing a workflow changes what the next part is judged against
   * with no routine reload and nothing in the record to say so. Without these,
   * two consecutive parts can be judged by different models and the records are
   * indistinguishable — which is a traceability problem for QA even when both
   * verdicts are correct.
   *
   * Optional so a record written by a build predating them still parses.
   */
  workflowId?: string;
  outputName?: string;
  /**
   * What the workflow's active-learning output reported for this frame.
   *
   * By default the upload block runs with `fire_and_forget` on and answers
   * that the upload was queued before it runs, so a failed upload (no network,
   * a Roboflow-side limit) never shows here. Only a workflow that turns
   * `fire_and_forget` off records the upload's own result.
   *
   * Absent when the workflow declares no such output, or from a vision-bot
   * predating the field. Its presence here never affected whether this record
   * was written.
   */
  activeLearningStatus?: string;
}

export type CapturedLocateResult = LocateResult & {
  /**
   * Set by the RunLocateSkill step: the pose already projected into the frame,
   * as the skill's overlays draw it. Null for a pose behind the camera. Absent
   * when the record has no `camera.cameraPose`.
   */
  grasp?: ProjectedGrasp | null;
};

export interface CaptureMetadata {
  imageFilename: string;
  botId: string;
  botAlias: string | undefined;
  timestamp: string;
  stepKind: CapturingStepKind;
  /**
   * Set by the RunLocateSkill and RunInspectSkill steps to record which saved
   * skill produced the results.
   */
  skillId?: string;
  /**
   * The named values in the frame's composed name, such as the step number and
   * routine variables, keyed by `nameValueKey`.
   */
  nameValues?: Record<string, string>;
  locateResults?: CapturedLocateResult[];
  /**
   * The camera behind the frame, which is what a reader needs to project poses
   * (robot frame) back onto the image. `cameraPose` is the camera's optical
   * frame in the robot base frame, in metres, with OpenCV axes (+Z along the
   * view, +X right, +Y down in the image). It is absent for a wrist camera
   * whose capture pose was unknown, and whenever nothing was detected.
   *
   * Set by the RunLocateSkill step only.
   */
  camera?: {
    intrinsics: CameraIntrinsicsExtended;
    cameraPose?: CartesianPose;
  };
  /** Set by the RunInspectSkill step. Mutually exclusive with `locateResults`. */
  inspectResults?: InspectResults;
  stepParameters: Record<string, unknown>;
}

/**
 * The sidecar path for a frame: its own path with `.png` swapped for
 * `_metadata.json`.
 *
 * Its own function because more than one writer derives it now — an upload to
 * storage and, for a local target, a filename in a directory — and the two
 * agreeing is what lets a reader find the record beside any frame regardless of
 * where it was written.
 */
export function metadataPathFor(remotePath: string): string {
  return remotePath.replace(/\.png$/i, '_metadata.json');
}

/**
 * Serialises a record to a temp file and hands back the path.
 *
 * Split out from `saveMetadataToStorage` so a caller writing to several targets
 * serialises once and uploads the same bytes to each, rather than re-encoding
 * per target. The caller owns the returned file and must unlink it.
 */
export async function serialiseRecordToTempFile(
  metadata: CaptureMetadata,
): Promise<string> {
  const tempFilename = `metadata_${Date.now()}_${Math.random().toString(36).substring(7)}.json`;
  const tempFilePath = path.join(os.tmpdir(), tempFilename);

  await fs.promises.writeFile(
    tempFilePath,
    JSON.stringify(metadata, null, 2),
    'utf-8',
  );

  return tempFilePath;
}

/**
 * Serialises a `CaptureMetadata` object as JSON and uploads it next to the
 * corresponding image.  The JSON path is derived from `remotePath` by
 * replacing the `.png` extension with `_metadata.json`.
 */
export async function saveMetadataToStorage(args: {
  metadata: CaptureMetadata;
  remotePath: string;
  storageEquipment: StorageEquipment;
}): Promise<void> {
  const { metadata, remotePath, storageEquipment } = args;

  const jsonRemotePath = metadataPathFor(remotePath);

  const tempFilePath = await serialiseRecordToTempFile(metadata);

  log.info('saveMetadataToStorage', 'Uploading metadata JSON', {
    jsonRemotePath,
  });

  try {
    await uploadToStorage({
      tempFilePath,
      remotePath: jsonRemotePath,
      storageEquipment,
    });
  } finally {
    await fs.promises.unlink(tempFilePath).catch((unlinkError) => {
      log.warn('saveMetadataToStorage', 'Failed to delete temporary file', {
        tempFilePath,
        error: unlinkError,
      });
    });
  }
}
